import { z } from 'zod';
import {
  SkillAuditEntrySchema,
  SkillBodySchema,
  SkillFrontmatterSchema,
  SkillSchema,
  SkillSlugSchema,
  SkillSourceSchema,
  SkillStatusSchema,
  SkillSummarySchema,
  SkillVersionSchema,
} from './skills.schema';

export const ListSkillsQuerySchema = z.object({
  applicationId: z.string().optional(),
  agentId: z.string().optional(),
  source: SkillSourceSchema.optional(),
  status: SkillStatusSchema.optional(),
  includeDeleted: z.coerce.boolean().optional().prefault(false),
  skip: z.coerce.number().int().nonnegative().optional().prefault(0),
  limit: z.coerce.number().int().positive().max(200).optional().prefault(50),
});
export type ListSkillsQuery = z.infer<typeof ListSkillsQuerySchema>;

export const SkillApplicationScopeQuerySchema = z.object({
  applicationId: z.string().optional(),
});
export type SkillApplicationScopeQuery = z.infer<
  typeof SkillApplicationScopeQuerySchema
>;

export const ListSkillsResponseSchema = z.object({
  skills: z.array(SkillSummarySchema),
  total: z.int().nonnegative(),
});
export type ListSkillsResponse = z.infer<typeof ListSkillsResponseSchema>;

export const GetSkillResponseSchema = SkillSchema;
export type GetSkillResponse = z.infer<typeof GetSkillResponseSchema>;

export const ListSkillVersionsResponseSchema = z.object({
  versions: z.array(SkillVersionSchema),
});
export type ListSkillVersionsResponse = z.infer<
  typeof ListSkillVersionsResponseSchema
>;

export const ListSkillAuditResponseSchema = z.object({
  entries: z.array(SkillAuditEntrySchema),
});
export type ListSkillAuditResponse = z.infer<
  typeof ListSkillAuditResponseSchema
>;

export const CreateSkillBodySchema = z.object({
  applicationId: z.string().min(1),
  agentId: z.string().optional(),
  slug: SkillSlugSchema,
  frontmatter: SkillFrontmatterSchema,
  body: SkillBodySchema,
  publishImmediately: z.boolean().optional().default(false),
});
export type CreateSkillBody = z.infer<typeof CreateSkillBodySchema>;

export const AiCreateSkillBodySchema = CreateSkillBodySchema;
export type AiCreateSkillBody = z.infer<typeof AiCreateSkillBodySchema>;

export const AiCreateSkillRejectedReasonSchema = z.enum([
  'unsafe_input',
  'out_of_scope',
  'unknown_tools',
  'generation_failed',
  'feature_disabled',
]);
export type AiCreateSkillRejectedReason = z.infer<
  typeof AiCreateSkillRejectedReasonSchema
>;

export const AiCreateSkillRejectedIssueSchema = z.object({
  category: z.enum([
    'prompt_injection',
    'system_override',
    'secret_exfiltration',
    'malware',
    'out_of_scope',
  ]),
  field: z.enum(['name', 'description', 'body']),
  severity: z.enum(['high', 'medium', 'low']),
  message: z.string(),
  excerpt: z.string().optional(),
});
export type AiCreateSkillRejectedIssue = z.infer<
  typeof AiCreateSkillRejectedIssueSchema
>;

export const AiCreateSkillRejectedResponseSchema = z.object({
  reason: AiCreateSkillRejectedReasonSchema,
  message: z.string(),
  details: z.array(z.string()).optional(),
  issues: z.array(AiCreateSkillRejectedIssueSchema).optional(),
});
export type AiCreateSkillRejectedResponse = z.infer<
  typeof AiCreateSkillRejectedResponseSchema
>;

export const UpdateSkillDraftBodySchema = z.object({
  frontmatter: SkillFrontmatterSchema,
  body: SkillBodySchema,
});
export type UpdateSkillDraftBody = z.infer<typeof UpdateSkillDraftBodySchema>;

export const PublishSkillBodySchema = z
  .object({
    versionId: z.string().optional(),
  })
  .optional();
export type PublishSkillBody = z.infer<typeof PublishSkillBodySchema>;

export const RollbackSkillBodySchema = z.object({
  versionId: z.string(),
});
export type RollbackSkillBody = z.infer<typeof RollbackSkillBodySchema>;

export const SkillMutationResponseSchema = SkillSchema;
export type SkillMutationResponse = z.infer<typeof SkillMutationResponseSchema>;

export const PlatformResyncResponseSchema = z.object({
  added: z.array(z.string()),
  updated: z.array(z.string()),
  tombstoned: z.array(z.string()),
  unchanged: z.int().nonnegative(),
  scannedRoot: z.string().nullable(),
});
export type PlatformResyncResponse = z.infer<
  typeof PlatformResyncResponseSchema
>;
